/**
 * Estrella WebAPI (vNext)
 * https://wiki.viessmann-climatesolutions.com/display/EWV/Equipment
 */
import { Injectable } from '@angular/core';

import { SnapshotType } from '@store';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ClientService, httpHeaders } from '../../client.service';
import { CursorPaging, GetResponse, Installation } from '../../models';
import { FeatureDeprecated, Features } from '../operational-data/operational-data.model';
import {
  CreateSnapshotResponse,
  SnapshotResponse,
  SnapshotsConfigurationsBackupResponse,
  SnapshotsResponse,
  SnapshotVddsResponse,
  Vdd,
  VddWithContent,
} from '../snapshots/snapshots.model';

import {
  AccessList,
  ActiveErrorResponse,
  DepartmentResponse,
  DeviceImageResponse,
  InstallationSummary,
  EquipmentDocumentResponse,
  MaintenancePackagesResponse,
  MaterialDetailsResponse,
  SrcLiteVoucher,
  SrcLiteVoucherStatus,
  VoucherType,
  DataWithCursorResponse,
  CreateCommercialInstallationPayload,
  CreateInstallationPayload,
  ResetGatewayResponse,
  EquipmentLiteDetail,
} from './equipment.model';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class EquipmentApi {
  private readonly baseUrl = 'equipment/v3';
  private readonly snapshotsBaseUrl = 'iot/v1/equipment/installations';

  constructor(private clientService: ClientService) {}

  /**
   * Returns list of all device features with all related to them `actions`, `components`, `properties` and metadata.
   * @param installationId
   * @param gatewaySerial
   * @param pairingId
   * @param params
   */
  listDeviceFeatures(
    installationId: number,
    gatewaySerial: string,
    pairingId: string,
    body: { filter?: string[]; regex?: string } = {}
  ): Observable<Features> {
    const endpoint = `iot/v2/features/installations/${installationId}/gateways/${gatewaySerial}/devices/${pairingId}/features/filter`;

    return this.clientService
      .post<{ data?: FeatureDeprecated[]; features?: FeatureDeprecated[] }>(endpoint, body)
      .pipe(map((res) => ({ data: res.data || res.features })));
  }

  /**
   * Get installation
   * @param params
   */
  getInstallation(id: number): Observable<GetResponse<Installation>> {
    const endpoint = `iot/v2/equipment/installations/${id}`;

    return this.clientService.get(endpoint, { includeGateways: 'true' });
  }

  /**
   * PUT installation
   * @param installationId
   * @param installationData
   */
  updateInstallation(
    installationId: number,
    installationData: Installation
  ): Observable<GetResponse<Installation>> {
    const endpoint = `iot/v2/equipment/installations/${installationId}`;

    return this.clientService.put(endpoint, {
      ...installationData,
    });
  }

  /**
   * POST installation
   * @param payload
   */
  createInstallation(payload: CreateInstallationPayload): Observable<GetResponse<Installation>> {
    const endpoint = `iot/v2/equipment/installations`;
    const body = payload;

    if (!payload.withoutOwner) {
      delete body.installationOwnerData;
    }

    return this.clientService.post(endpoint, body);
  }

  /**
   * POST commercial installation
   * @param payload
   */
  createCommercialInstallation(
    payload: CreateCommercialInstallationPayload
  ): Observable<GetResponse<Installation>> {
    const endpoint = `iot/v2/equipment/commercial/installations`;

    return this.clientService.post(endpoint, payload);
  }

  /**
   *
   * @param id
   */
  getInstallationAccessList(id: number): Observable<GetResponse<AccessList>> {
    const endpoint = `iot/v3/equipment/installations/${id}/accessList`;

    return this.clientService.get(endpoint);
  }

  /**
   * Returns material name and description based on first 7 digits of device serial.
   * The supplied serials will not be transformed in any form, so you need to supply only proper 7 digits.
   * Texts values returned on this endpoint are translated.
   * Language of translations can be configured using Accept-Language header.
   * @param boilerSerial
   */
  getMaterialDetails(boilerSerial: string): Observable<MaterialDetailsResponse> {
    const materials = this.getMaterialSlug(boilerSerial);
    const endpoint = `${this.baseUrl}/material-details?materials=${materials}`;

    return this.clientService.get(endpoint, {}, httpHeaders);
  }

  getMaterialGatewayName(gatewaySerial: string): Observable<MaterialDetailsResponse> {
    const gatewayPrefix = gatewaySerial.slice(0, 7);
    const endpoint = `${this.baseUrl}/material-details?materials=${gatewayPrefix}`;

    return this.clientService.get(endpoint, {}, httpHeaders);
  }

  updateDevice(
    installationId: number,
    gatewaySerial: string,
    deviceId: string,
    boilerSerial: string
  ): Observable<{}> {
    const endpoint = `iot/v2/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices/${deviceId}`;

    return this.clientService.put(endpoint, { boilerSerial });
  }

  updateBmuSerial(
    installationId: number,
    gatewaySerial: string,
    deviceId: string,
    bmuSerial: string
  ): Observable<{}> {
    const endpoint = `iot/v2/equipment/installations/${installationId}/gateways/${gatewaySerial}/devices/${deviceId}`;

    return this.clientService.put(endpoint, { bmuSerial });
  }

  /**
   * Returns list of events for Active Error
   * @param installationId
   * @param params
   */
  listActiveError(
    installationId: string | number
  ): Observable<DataWithCursorResponse<ActiveErrorResponse[]>> {
    const endpoint = `iot/v1/smart-notes/installations/${installationId}/smartNotes`;

    return this.clientService.get(endpoint, {}, httpHeaders);
  }

  getAllDepartments(): Observable<DepartmentResponse> {
    const endpoint = `iot/v1/equipment/departments`;

    return this.clientService.get(endpoint);
  }

  deleteDepartment(installationId: string): Observable<DepartmentResponse> {
    const endpoint = `iot/v1/equipment/installations/${installationId}/departmentAccess`;

    return this.clientService.delete(endpoint);
  }

  updateDepartment(installationId: string, departmentName: string): Observable<DepartmentResponse> {
    const endpoint = `iot/v1/equipment/installations/${installationId}/departmentAccess`;

    return this.clientService.put(endpoint, { name: departmentName });
  }

  refreshDeviceList(
    installationId: number,
    gatewaySerial: string
  ): Observable<ResetGatewayResponse> {
    const endpoint = `iot/v1/equipment/installations/${installationId}/gateways/${gatewaySerial}/reset`;

    return this.clientService.post(endpoint, {});
  }

  getMaintenancePackages(
    boilerSerial: string,
    vkorg: string
  ): Observable<MaintenancePackagesResponse> {
    if (!vkorg) {
      return of({
        link: '',
      });
    }

    const devicePrefix = boilerSerial?.slice(0, 7);
    const endpoint = `${this.baseUrl}/maintenance-packages/${devicePrefix}?vkorg=${vkorg}`;

    return this.clientService.get(endpoint, {}, httpHeaders);
  }

  /**
   * Returns maintenance checklist pdf
   * @param boilerId
   * @param countryCode
   * @param languageCode
   */
  getMaintenanceChecklist(
    boilerId: string,
    countryCode: string = 'DE',
    languageCode: string = 'de'
  ): Observable<EquipmentDocumentResponse[]> {
    const endpoint = `${this.baseUrl}/vibooks/checklist/${boilerId}`;

    return this.clientService.get(endpoint, { countryCode, languageCode }, httpHeaders);
  }

  /**
   * Returns list of documents of given type
   * @param materialNumber
   * @param documentTypes
   * @param countryCode
   * @param languageCode
   */
  getDocumentsOfTypes(
    materialNumber: string,
    documentTypeParams: string,
    countryCode: string = 'DE',
    languageCode: string = 'de'
  ): Observable<EquipmentDocumentResponse[]> {
    const endpoint = `backend/pim/v1/products/${languageCode}/${countryCode}/documents?${documentTypeParams}`;

    return this.clientService.get(
      endpoint,
      {
        materialNumber,
      },
      httpHeaders
    );
  }

  /**
   * Returns image of device
   * @param boilerSerial
   * @param fromat
   */
  getDeviceImage(boilerSerial: string, format: string): Observable<DeviceImageResponse> {
    const endpoint = `${this.baseUrl}/images?materials=${boilerSerial}&format=${format}`;

    return this.clientService.get(endpoint);
  }

  generateSrcLiteVoucher(
    voucherType: VoucherType,
    companyNumber?: string
  ): Observable<SrcLiteVoucher> {
    const endpoint = `${this.baseUrl}/vouchers`;

    const params = {
      voucherType,
    };

    if (companyNumber) {
      params['companyNumber'] = companyNumber;
    }

    return this.clientService.post(endpoint, params);
  }

  validateSrcLiteVoucher(voucher: string): Observable<SrcLiteVoucherStatus> {
    const endpoint = `${this.baseUrl}/vouchers/${voucher}`;

    return this.clientService.get(endpoint);
  }

  equipmentSummary(boilerSerial: string): Observable<InstallationSummary> {
    const endpoint = `${this.baseUrl}/summary?serial=${boilerSerial}`;

    return this.clientService.get(endpoint);
  }

  validateBoilerSerial(boilerSerial: string): Observable<void> {
    const endpoint = `${this.baseUrl}/serial-numbers/validate/${boilerSerial}`;

    return this.clientService.get(endpoint);
  }

  ////////////////////////////////////////
  // SNAPSHOTS
  // https://wiki.viessmann-climatesolutions.com/pages/viewpage.action?spaceKey=EWV&title=%5BIN+PROGRESS%5D+Snapshots
  //////////////////////////////////////////

  getInstallationVddList(
    installationId: string,
    gatewaySerial: string
  ): Observable<SnapshotsConfigurationsBackupResponse> {
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots/configurations/backup`;

    return this.clientService.get(endpoint);
  }

  getSnapshots(
    installationId: string,
    gatewaySerial: string,
    type: SnapshotType
  ): Observable<SnapshotsResponse> {
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots?type=${type}`;

    return this.clientService.get(endpoint);
  }

  getSnapshot(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string
  ): Observable<SnapshotResponse> {
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots/${snapshotId}`;

    return this.clientService.get(endpoint);
  }

  getSnapshotVdds(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string
  ): Observable<SnapshotVddsResponse> {
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots/${snapshotId}/vdds`;

    return this.clientService.get(endpoint);
  }

  createSnapshot(
    installationId: string,
    gatewaySerial: string,
    vddList: Vdd[],
    snapshotType: SnapshotType
  ): Observable<CreateSnapshotResponse> {
    // TODO: we currenently hardcode version, when backend makes version parameter optional then we can remove it.
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots`;

    return this.clientService.post(endpoint, {
      vdds: vddList,
      version: '1.0.0',
      type: snapshotType,
    });
  }

  updateSnapshot(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string,
    vdds: VddWithContent[]
  ): Observable<{}> {
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots/${snapshotId}/vdds`;

    return this.clientService.post(endpoint, { vdds });
  }

  cancelVdd(
    installationId: string,
    gatewaySerial: string,
    snapshotId: string,
    key: string
  ): Observable<{}> {
    const endpoint = `${this.snapshotsBaseUrl}/${installationId}/gateways/${gatewaySerial}/snapshots/${snapshotId}/vdds/${key}/cancel`;

    return this.clientService.post(endpoint, {});
  }

  getUserInstallationsCollection(
    userId: string,
    params: CursorPaging = {}
  ): Observable<DataWithCursorResponse<Installation[]>> {
    const endpoint = `iot/v2/equipment/users/${userId}/installations`;

    return this.clientService.get(endpoint, params, httpHeaders);
  }

  private getMaterialSlug(deviceIdentification: string): string {
    return deviceIdentification === 'solarlog'
      ? deviceIdentification
      : deviceIdentification.substring(0, 7);
  }

  liteDetail(serial: string): Observable<EquipmentLiteDetail> {
    const endpoint = 'equipment/v3/lite-detail';
    const httpParams = new HttpParams().append('serialNumber', serial);

    return this.clientService.get<EquipmentLiteDetail>(endpoint, httpParams);
  }
}
